import { __assign } from "tslib";
// DS: CR28
import React, { useState, useCallback, useRef, useMemo } from 'react';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import { Url } from '@wix/da-url';
import LoadingIndicator from '../../../LoadingIndicator';
import Button from '../../../Button';
import Flex from '../../../Flex';
import { ThemeOverride } from '../../../Context';
import { ThemeOptions } from '../../../publicSession/types';
import { IconSize } from '../../../Icons/IconWrap';
import BellIcon from '../../../Icons/Bell';
import { useTranslation } from 'react-i18next';
import { BiLink, BiButton, BiEventType } from '../../../biLogger';
import VerificationReminder from '../../_partials/VerificationReminder';
import LiveSettingsDropdown from '../../_partials/LiveSettingsDropdown';
import { useFlag } from '../../../redux/flags/hooks';
import { useOnOutsideClick } from '../../../utils/hooks/useOnOutsideClick';
import noop from '../../../utils/noop';
import ReloadButton from '../../_partials/ReloadButton';
import TextTooltipOnHover from '../../../Popover/TextTooltipOnHover';
import { logBiEvent } from '../../../biLogger/redux/saga';
import SiteHeaderStyles from '../SiteHeader.scss';
import s from './NotificationsDropdown.scss';
var NotificationsDropdown = function (_a) {
    var verificationEmail = _a.verificationEmail, feedbackCount = _a.feedbackCount, isOpen = _a.isOpen, newFeedbackCount = _a.newFeedbackCount, hasBeenOpened = _a.hasBeenOpened, onMouseEnter = _a.onMouseEnter, onClick = _a.onClick, onTouchEnd = _a.onTouchEnd, biData = _a.biData, href = _a.href, isLegacyHeader = _a.isLegacyHeader, _b = _a.onMouseLeave, onMouseLeave = _b === void 0 ? noop : _b, resetNewFeedback = _a.resetNewFeedback, toggleNotificationsDropdown = _a.toggleNotificationsDropdown, ariaLabel = _a["aria-label"];
    var t = useTranslation().t;
    var _c = useState(true), isLoading = _c[0], setIsLoading = _c[1];
    var _d = useState(false), isLiveSettingsDropdownOpen = _d[0], setLiveSettingnsDropdownOpen = _d[1];
    var hideNotificationDropdown = useFlag('skip_notification_dropdown');
    // no point to spamming this if our guest has twitchy hands
    var throttledMouseLeave = useMemo(function () { return throttle(onMouseLeave, 300); }, [
        onMouseLeave,
    ]);
    var rootRef = useRef(null);
    var iframeRef = useRef(null);
    var onClose = useCallback(function (event) {
        if (event === void 0) { event = {}; }
        setLiveSettingnsDropdownOpen(false);
        throttledMouseLeave(event);
    }, [throttledMouseLeave]);
    var handleClickOutside = useCallback(function () {
        if (!isOpen) {
            return;
        }
        onClose();
    }, [isOpen, onClose]);
    useOnOutsideClick(rootRef, handleClickOutside);
    var notificationsUrl = Url.feedbackLink();
    // It's inaccessible to use a button if we only need to link somewhere.
    var BiComponent = onClick ? BiButton : BiLink;
    var handleOnLoad = function () {
        // note: this will fire if src is not yet set (or even when the src attribute is missing entirely)
        // so we wait until it's loading our src document to stop showing the loading indicator
        if (isOpen || hasBeenOpened) {
            setIsLoading(false);
            if (typeof document !== 'undefined' &&
                typeof window !== 'undefined' &&
                window.location.host.indexOf('deviantart.com') !== -1) {
                document.domain = 'deviantart.com';
            }
        }
    };
    var reloadIframe = useCallback(function () {
        var iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
            setIsLoading(true);
            resetNewFeedback();
            iframe.contentWindow.location.reload();
        }
    }, [iframeRef, setIsLoading, resetNewFeedback]);
    if (hideNotificationDropdown) {
        return (React.createElement("div", { className: classnames(SiteHeaderStyles['icon-button-has-submenu'], SiteHeaderStyles['icon-button-has-notifications-submenu']), ref: rootRef, onMouseEnter: function () {
                return logBiEvent({
                    evid: BiEventType.PAGEVIEW,
                    view: 'notifications',
                    component: 'tooltip',
                });
            } },
            React.createElement(TextTooltipOnHover, { tooltipText: t('siteHeader.notificationsMenu.tooltip') },
                React.createElement(BiComponent, { biData: biData, className: classnames(SiteHeaderStyles['icon-button'], SiteHeaderStyles['bell-button']), href: href, onClick: onClick, "aria-label": ariaLabel },
                    React.createElement("span", null,
                        React.createElement(BellIcon, { className: SiteHeaderStyles['notifications-icon'], size: IconSize.MEDIUM }),
                        feedbackCount !== '0' && (React.createElement("span", { className: SiteHeaderStyles['number'] }, feedbackCount)))))));
    }
    return (React.createElement("div", { className: classnames(SiteHeaderStyles['icon-button-has-submenu'], SiteHeaderStyles['icon-button-has-notifications-submenu']), onMouseEnter: onMouseEnter, onMouseLeave: onClose, onTouchEnd: onTouchEnd, ref: rootRef },
        React.createElement(Button, { view: "b3", className: s['notifications-button'], "aria-haspopup": true, "aria-expanded": isOpen, "aria-controls": "site-header-notifications", onClick: toggleNotificationsDropdown }, t('siteHeader.notificationsMenu.open')),
        React.createElement(BiComponent, { biData: biData, className: classnames(SiteHeaderStyles['icon-button'], SiteHeaderStyles['bell-button'], isOpen && SiteHeaderStyles['active']), href: href, onClick: onClick, "aria-label": ariaLabel },
            React.createElement("span", null,
                React.createElement(BellIcon, { className: SiteHeaderStyles['notifications-icon'], size: IconSize.MEDIUM }),
                feedbackCount !== '0' && (React.createElement("span", { className: SiteHeaderStyles['number'] }, feedbackCount)))),
        React.createElement("div", { role: "dialog", id: "site-header-notifications", className: s['notifications-dropdown-wrapper'] },
            React.createElement(ThemeOverride, { theme: ThemeOptions.LIGHT, className: classnames(s['container'], isOpen && s['active'], isLiveSettingsDropdownOpen && s['live-settings-open']) },
                React.createElement(Flex, { justifyContent: "space-between", alignItems: "center", className: s['title-container'] },
                    React.createElement("span", { className: s['title'] }, t('siteHeader.notificationsMenu.titleMobile')),
                    React.createElement(LiveSettingsDropdown, { onOpen: function () { return setLiveSettingnsDropdownOpen(true); }, onClose: function () { return setLiveSettingnsDropdownOpen(false); }, isOpen: isLiveSettingsDropdownOpen })),
                newFeedbackCount > 0 && (React.createElement(ReloadButton, { onClick: reloadIframe, count: newFeedbackCount })),
                React.createElement("div", { className: s['separator'] }),
                React.createElement(Flex, { direction: "column", className: s['notifications-frame-wrapper'] },
                    !!verificationEmail && (React.createElement(VerificationReminder, { email: verificationEmail, hideChangeEmail: true })),
                    React.createElement("iframe", __assign({ ref: iframeRef, title: "Eclipse notifications", id: "eclipse-notifications-iframe", className: s['notifications-frame'] }, ((isOpen || hasBeenOpened) && {
                        src: Url.notificationsHeaderLink() + "?legacy=" + isLegacyHeader,
                    }), { frameBorder: "0", scrolling: isLoading ? 'no' : 'yes', onLoad: handleOnLoad }))),
                isLoading && (React.createElement(LoadingIndicator, { view: "spinner", className: s['loader'] })),
                React.createElement(Button, { href: notificationsUrl, className: s['footer-see-all'], target: "_top", view: "b81", biData: {
                        evid: BiEventType.TOP_NAV_MENU_CLICK,
                        menu_name: 'notifications',
                        menu_item: 'see_all',
                    } }, t('siteHeader.notificationsMenu.seeAll'))))));
};
NotificationsDropdown.displayName = 'NotificationsDropdown';
export default NotificationsDropdown;
//# sourceMappingURL=NotificationsDropdown.js.map